import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import ReportFilterBar from "./ReportFilterBar";
import ReportTable from "./ReportTable";
import Pagination from "../Pagination/Pagination";
import { getCourierReport } from "../../api/reports";

import { formatINR, formatNumber, sumBy } from "./reportUtils";

const formatPickupDate = (value) => {
  if (!value) return "—";

  const date = new Date(
    /^\d{4}-\d{2}-\d{2}$/.test(value)
      ? `${value}T00:00:00+05:30`
      : value,
  );
  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
};

const CourierReport = () => {
  const [, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState({
    from: "",
    to: "",
    q: "",
    page: 1,
    limit: 10,
    sortBy: "pickupDate",
    sortOrder: "desc",
    tab: "all",
    courier: "",
    status: "",
  });

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });

  useEffect(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  // Fetch courier report
  useEffect(() => {
    const fetchCourierReport = async () => {
      try {
        setLoading(true);

        const response = await getCourierReport({
          from: filters.from,
          to: filters.to,
          q: filters.q,
          page: filters.page,
          limit: filters.limit,
          sortBy: filters.sortBy,
          sortOrder: filters.sortOrder,
          tab: filters.tab,
          courier: filters.courier,
          status: filters.status,
        });

        if (response?.success) {
          setRows(response?.data?.rows || []);

          setPagination(
            response?.pagination || {
              page: filters.page,
              limit: filters.limit,
              total: response?.data?.rows?.length || 0,
              totalPages: 1,
            },
          );
        } else {
          setRows([]);
          setPagination({
            page: 1,
            limit: filters.limit,
            total: 0,
            totalPages: 1,
          });
        }
      } catch (error) {
        console.error("Courier report API error:", error);

        setRows([]);

        setPagination({
          page: 1,
          limit: filters.limit,
          total: 0,
          totalPages: 1,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCourierReport();
  }, [
    filters.from,
    filters.to,
    filters.q,
    filters.page,
    filters.limit,
    filters.sortBy,
    filters.sortOrder,
    filters.tab,
    filters.courier,
    filters.status,
  ]);

  const totals = {
    declaredWeight: {
      value: sumBy(rows, "declaredWeight"),
      format: "number",
    },

    chargedWeight: {
      value: sumBy(rows, "chargedWeight"),
      format: "number",
    },

    freightCost: {
      value: sumBy(rows, "freightCost"),
      format: "inr",
    },

    customerShippingCharge: {
      value: sumBy(rows, "customerShippingCharge"),
      format: "inr",
    },

    gainLoss: {
      value: sumBy(rows, "gainLoss"),
      format: "inr",
    },
  };

  const columns = [
    {
      key: "orderId",
      label: "Order ID",
      sortable: true,
    },
    {
      key: "pickupDate",
      label: "Pickup Date",
      sortable: true,
      render: (row) => formatPickupDate(row.pickupDate),
    },

    {
      key: "courier",
      label: "Courier",
      sortable: true,
      minWidth: 160,
    },
    {
      key: "declaredWeight",
      label: "Declared (kg)",
      sortable: true,
      render: (row) => formatNumber(row.declaredWeight),
    },
    {
      key: "chargedWeight",
      label: "Charged (kg)",
      sortable: true,
      render: (row) => (
        <span className={row.chargedWeight > row.declaredWeight ? "report-loss" : ""}>
          {formatNumber(row.chargedWeight)}
        </span>
      ),
    },
    {
      key: "customerShippingCharge",
      label: "Shipping Charge",
      sortable: true,
      render: (row) => formatINR(row.customerShippingCharge),
    },
    {
      key: "gainLoss",
      label: "Gain/Loss",
      sortable: true,
      render: (row) => (
        <span className={row.gainLoss < 0 ? "report-loss" : "report-profit"}>
          {formatINR(row.gainLoss)}
        </span>
      ),
    },
  ];

  const handleTabChange = (tab) => {
    setFilters((prev) => ({
      ...prev,
      tab,
      page: 1,
    }));
  };

  const handleCourierChange = (e) => {
    setFilters((prev) => ({
      ...prev,
      courier: e.target.value,
      page: 1,
    }));
  };

  const handleStatusChange = (e) => {
    setFilters((prev) => ({
      ...prev,
      status: e.target.value,
      page: 1,
    }));
  };

  const handleSort = (sortBy, sortOrder) => {
    setFilters((prev) => ({
      ...prev,
      sortBy,
      sortOrder,
      page: 1,
    }));
  };

  const handlePageChange = (page) => {
    setFilters((prev) => ({
      ...prev,
      page,
    }));
  };

  return (
    <div className="container-fluid p-4 report-page">
      <div className="card p-3">
        {/* Filters */}
        <ReportFilterBar
          filters={filters}
          onChange={setFilters}
          onReset={setFilters}
          searchPlaceholder="Search order, AWB, courier or customer..."
          filterSlotInline
          actionSlot={
            <button type="button" className="btn btn-success w-100" onClick={() => {}}>
              Export CSV
            </button>
          }
        />

        {/* Table */}
        <ReportTable
          columns={columns}
          rows={rows}
          totals={totals}
          loading={loading}
          sortBy={filters.sortBy}
          sortOrder={filters.sortOrder}
          onSort={handleSort}
          rowKey="awb"
        />
      </div>

      <Pagination
        totalItems={pagination.total}
        itemsPerPage={pagination.limit}
        currentPage={pagination.page}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

export default CourierReport;
